import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";

export const Tabs = TabsPrimitive.Root;

export const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    // Divider painted as an inset shadow (not border-b + item -mb-px): the
    // old overlap idiom makes triggers 1px taller than the list, which turns
    // into a vertical scrollbar the moment the list scrolls horizontally.
    className={cn(
      "inline-flex items-center gap-1 shadow-[inset_0_-1px_0_0_rgb(var(--of-border-muted))]",
      className,
    )}
    {...props}
  />
));
TabsList.displayName = "TabsList";

/**
 * Primer underlined tabs — the underline aligns with the row bottom border,
 * so transitions feel like GitHub's tab strip.
 */
export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex h-9 shrink-0 items-center gap-1.5 px-2 text-sm whitespace-nowrap text-muted-foreground",
      "border-b-2 border-transparent transition-colors duration-100",
      "hover:text-foreground",
      "data-[state=active]:text-foreground data-[state=active]:border-[rgb(var(--of-fg-default))] data-[state=active]:font-semibold",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 rounded-sm",
      className,
    )}
    {...props}
  />
));
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn("mt-4 outline-none", className)} {...props} />
));
TabsContent.displayName = "TabsContent";
